/**
 * Custom hook for managing remediation list functionality
 * Handles add/remove operations, bulk actions, and local state management
 * with optimistic UI updates
 */
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import $ from "jquery";
import {
  addRemediationData,
  removeRemediationData,
  remediationDataTotal,
} from "../actions/sub-domain";

export const useRemediationActions = (
  collectionName,
  organizationId,
  currentTableData = []
) => {
  const dispatch = useDispatch();

  // Redux selectors
  const remediationList = useSelector(
    (state) => state?.auth?.remediationListAll?.data?.data
  );

  // Local state management
  const [addedRemediations, setAddedRemediations] = useState({});
  const [localRemediationStatus, setLocalRemediationStatus] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [multiCheckTrue, setMultiCheckTrue] = useState(false);

  // Update remediation list when it changes
  useEffect(() => {
    if (remediationList) {
      const updatedRemediations = remediationList.reduce((results, elem) => {
        return { ...results, [elem._id]: elem };
      }, {});
      setAddedRemediations(updatedRemediations);
    }
  }, [remediationList]);

  // Clear local state for items where database state matches local expectation
  useEffect(() => {
    if (currentTableData && Object.keys(localRemediationStatus).length > 0) {
      const toRemove = {};

      Object.keys(localRemediationStatus).forEach((itemId) => {
        const dbItem = currentTableData.find((item) => item._id === itemId);
        if (dbItem) {
          const localExpectation = localRemediationStatus[itemId];
          const dbState = addedRemediations[itemId] ? true : false;

          if (localExpectation === dbState) {
            toRemove[itemId] = true;
          }
        }
      });

      if (Object.keys(toRemove).length > 0) {
        setLocalRemediationStatus((prev) => {
          const newState = { ...prev };
          Object.keys(toRemove).forEach((itemId) => {
            delete newState[itemId];
          });
          return newState;
        });
      }
    }
  }, [currentTableData, addedRemediations, localRemediationStatus]);

  // Selection handlers
  const multicheck = (data) => {
    const allIds = data?.map((item) => item._id);
    if (selectedIds.length > 0 || selectedIds.length == 0) {
      setMultiCheckTrue(true);
      setSelectedIds(allIds);
    }
    if (multiCheckTrue == true) {
      setSelectedIds([]);
      setMultiCheckTrue(false);
    }
  };

  const singleClick = (id) => {
    setSelectedIds((prevSelectedIds) => {
      if (prevSelectedIds.includes(id)) {
        return prevSelectedIds.filter((selectedId) => selectedId !== id);
      } else {
        return [...prevSelectedIds, id];
      }
    });
    if (selectedIds.length > 10) {
      setMultiCheckTrue(true);
    } else {
      setMultiCheckTrue(false);
    }
  };

  const clearSelections = () => {
    setSelectedIds([]);
    setMultiCheckTrue(false);
  };

  // Add single item to remediation
  const addToCart = (row, remediationType) => (e) => {
    let data = {
      collectionName: collectionName,
      organisationId: organizationId,
      recordId: row?._id,
    };
    if (remediationType) {
      data = { ...data, moduleName: remediationType };
    }

    $.confirm({
      title: "Are you sure?",
      content: "Do you really want to add this to the Remediation List Panel?",
      type: "green",
      buttons: {
        ok: {
          text: "Add",
          btnClass: "btn-green",
          keys: ["enter"],
          action: function () {
            e.preventDefault();

            // Update local state immediately
            setLocalRemediationStatus((prev) => ({
              ...prev,
              [row._id]: true,
            }));

            addRemediationData(data, dispatch)
              .then((res) => {
                window.location.reload();
                if (res.success) {
                  // console.log(`Add operation successful for ${row._id}`);
                  remediationDataTotal(dispatch, organizationId);
                } else {
                  // console.log("addRemediationData failed:", res);
                  // Revert the change if API call failed
                  setLocalRemediationStatus((prev) => ({
                    ...prev,
                    [row._id]: false,
                  }));
                }
              })
              .catch((error) => {
                // Revert on error
                setLocalRemediationStatus((prev) => ({
                  ...prev,
                  [row._id]: false,
                }));
              });
          },
        },
        cancel: function () {},
      },
    });
  };

  // Remove single item from remediation
  const removeFromCart = (_id, Fill_Grid) => (e) => {
    $.confirm({
      title: "Are you sure?",
      content:
        "Do you really want to remove this from the Remediation List Panel?",
      type: "red",
      buttons: {
        ok: {
          text: "Remove",
          btnClass: "btn-danger",
          keys: ["enter"],
          action: function () {
            e.preventDefault();

            // Update local state immediately
            setLocalRemediationStatus((prev) => ({
              ...prev,
              [_id]: false,
            }));

            removeRemediationData(_id, dispatch, organizationId)
              .then((res) => {
                window.location.reload();
                if (res.success) {
                  // console.log(`Remove operation successful for ${_id}`);
                  remediationDataTotal(dispatch, organizationId);
                } else {
                  //  console.log("removeRemediationData failed:", res);
                  // Revert the change if API call failed
                  setLocalRemediationStatus((prev) => ({
                    ...prev,
                    [_id]: true,
                  }));
                }
                if (Fill_Grid) {
                  Fill_Grid();
                }
              })
              .catch((error) => {
                // Revert on error
                setLocalRemediationStatus((prev) => ({
                  ...prev,
                  [_id]: true,
                }));
              });
          },
        },
        cancel: function () {},
      },
    });
  };

  // Bulk add selected items
  const bulkAddToCart = () => {
    if (selectedIds.length === 0) return;

    // Filter to only items that can be added (not already processed or in remediation)
    const addableIds = selectedIds.filter((id) => {
      const localValue = localRemediationStatus[id];
      const isInRemediation =
        addedRemediations[id] ||
        currentTableData.find((row) => row._id === id)?.isProcessed;
      return localValue !== undefined ? localValue === false : !isInRemediation;
    });

    if (addableIds.length === 0) {
      $.alert({
        title: "Cannot Add",
        content:
          "Selected items are already in the remediation system or processed. Items already processed cannot be re-added.",
        type: "red",
      });
      return;
    }

    $.confirm({
      title: "Are you sure?",
      content: `Do you really want to add ${addableIds.length} selected items to the Remediation List Panel?`,
      type: "green",
      buttons: {
        ok: {
          text: "Add All",
          btnClass: "btn-green",
          keys: ["enter"],
          action: function () {
            // Update local state immediately for addable items only
            const statusUpdates = {};
            addableIds.forEach((id) => {
              statusUpdates[id] = true;
            });
            setLocalRemediationStatus((prev) => ({
              ...prev,
              ...statusUpdates,
            }));

            // Process each addable item
            const promises = addableIds.map((recordId) => {
              const data = {
                collectionName: collectionName,
                organisationId: organizationId,
                recordId: recordId,
              };
              return addRemediationData(data, dispatch);
            });

            Promise.allSettled(promises).then((results) => {
              // Check for any failures and revert those
              const failedIds = [];
              results.forEach((result, index) => {
                if (result.status === "rejected" || !result.value?.success) {
                  failedIds.push(addableIds[index]);
                }
              });

              if (failedIds.length > 0) {
                // Revert failed items
                const revertUpdates = {};
                failedIds.forEach((id) => {
                  revertUpdates[id] = false;
                });
                setLocalRemediationStatus((prev) => ({
                  ...prev,
                  ...revertUpdates,
                }));
              }

              // Clear selection after bulk operation
              clearSelections();

              // Refresh counters
              remediationDataTotal(dispatch, organizationId);
            });
          },
        },
        cancel: function () {},
      },
    });
  };

  // Bulk remove selected items
  const bulkRemoveFromCart = () => {
    // Filter to only items that can be removed
    const removableIds = selectedIds.filter((id) => {
      const localValue = localRemediationStatus[id];
      const isInUnprocessedList =
        addedRemediations[id] && !addedRemediations[id].isProcessed;
      const isProcessed = currentTableData.find(
        (row) => row._id === id
      )?.isProcessed;

      return localValue === true || (isInUnprocessedList && !isProcessed);
    });

    if (removableIds.length === 0) {
      $.alert({
        title: "Cannot Remove",
        content:
          "Selected items are either already processed or not in the remediation list. Items already processed need to be removed manually in Remediation Mode",
        type: "red",
      });
      return;
    }

    $.confirm({
      title: "Remove Items",
      content: `Are you sure you want to remove ${removableIds.length} selected item(s) from the remediation list?`,
      type: "orange",
      typeAnimated: true,
      buttons: {
        ok: {
          text: "Remove All",
          btnClass: "btn-orange",
          keys: ["enter"],
          action: function () {
            // Update local state immediately for removable items
            const statusUpdates = {};
            removableIds.forEach((id) => {
              statusUpdates[id] = false;
            });
            setLocalRemediationStatus((prev) => ({
              ...prev,
              ...statusUpdates,
            }));

            // Process each removable item
            const promises = removableIds.map((recordId) => {
              return removeRemediationData(recordId, dispatch, organizationId);
            });

            Promise.allSettled(promises).then((results) => {
              // Check for any failures and revert those
              const failedIds = [];
              results.forEach((result, index) => {
                if (result.status === "rejected" || !result.value?.success) {
                  failedIds.push(removableIds[index]);
                }
              });

              if (failedIds.length > 0) {
                // Revert failed items
                const revertUpdates = {};
                failedIds.forEach((id) => {
                  revertUpdates[id] = true;
                });
                setLocalRemediationStatus((prev) => ({
                  ...prev,
                  ...revertUpdates,
                }));
              }

              // Clear selection after bulk operation
              clearSelections();

              // Refresh counters
              remediationDataTotal(dispatch, organizationId);
            });
          },
        },
        cancel: function () {},
      },
    });
  };

  // Helper function to determine if item should show plus or minus button
  const shouldShowPlusButton = (row) => {
    const localValue = localRemediationStatus[row?._id];
    const dbValue = addedRemediations[row?._id] ? true : false;
    const isProcessedInDB = row?.isProcessed === true;
    const isInRemediationSystem = dbValue || isProcessedInDB;

    // console.log("shouldShowPlusButton", localValue, dbValue, row?.isProcessed);

    return localValue !== undefined
      ? localValue === false
      : !isInRemediationSystem;
  };

  // Helper function to get items that can be added (for bulk operations)
  const getItemsToAdd = () => {
    return selectedIds.filter((id) => {
      const localValue = localRemediationStatus[id];
      const isInRemediation =
        addedRemediations[id] ||
        currentTableData.find((row) => row._id === id)?.isProcessed;
      return localValue !== undefined ? localValue === false : !isInRemediation;
    });
  };

  // Helper function to get items that can be removed (for bulk operations)
  const getItemsToRemove = () => {
    return selectedIds.filter((id) => {
      const localValue = localRemediationStatus[id];
      const isInRemediation =
        addedRemediations[id] ||
        currentTableData.find((row) => row._id === id)?.isProcessed;
      return localValue !== undefined ? localValue === true : isInRemediation;
    });
  };

  // Clear local state when page/category changes
  const clearLocalState = () => {
    setLocalRemediationStatus({});
  };

  return {
    // State
    selectedIds,
    multiCheckTrue,
    localRemediationStatus,
    addedRemediations,

    // Selection handlers
    multicheck,
    singleClick,
    clearSelections,

    // Add/Remove handlers
    addToCart,
    removeFromCart,
    bulkAddToCart,
    bulkRemoveFromCart,

    // Helper functions
    shouldShowPlusButton,
    getItemsToAdd,
    getItemsToRemove,
    clearLocalState,
  };
};
